"use client"

// CodeBlock: theme-aware syntax-highlighted code with a copy button. Reuses the
// Prism highlighter already in the bundle; the highlight theme follows light/dark.

import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { oneLight, oneDark } from "react-syntax-highlighter/dist/esm/styles/prism"
import { Check, Copy } from "lucide-react"
import { useState, type ReactNode } from "react"
import { useIsDark } from "@/components/theme/theme"

/**
 * Render a fenced code block with syntax highlighting and a copy button.
 *
 * @param code - the source code to display.
 * @param language - Prism language id (default "python").
 */
export function CodeBlock({
  code,
  language = "python",
}: {
  code: string
  language?: string
}): ReactNode {
  const isDark = useIsDark()
  const [copied, setCopied] = useState<boolean>(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="overflow-hidden rounded-md border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border bg-muted px-3 py-1">
        <span className="text-xs text-muted-foreground">{language}</span>
        <button
          type="button"
          onClick={handleCopy}
          aria-label="Copiar código"
          className="p-1 text-muted-foreground transition-colors hover:text-foreground"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
      <SyntaxHighlighter
        style={isDark ? oneDark : oneLight}
        language={language}
        PreTag="div"
        customStyle={{
          margin: 0,
          padding: "0.75rem",
          fontSize: "0.8rem",
          background: "transparent",
        }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  )
}
